import { useUserPermissions } from "@saleor/auth/hooks/useUserPermissions";
import Container from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import {
  PermissionEnum,
  useFailedFilesQuery,
  useRetryUploadMutation
} from "@saleor/graphql";
import useListSettings from "@saleor/hooks/useListSettings";
import useLocalPaginator, {
  useLocalPaginationState
} from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import { sectionNames } from "@saleor/intl";
import { getSortPoUploadFilterTable } from "@saleor/poUpload/sort";
import { ListViews } from "@saleor/types";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import React, { FC, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import POFailFileTable from "../components/POFailFileTable";
import POUploadPage from "../components/POUploadPage";
import RetrySuccessModal from "../components/RetrySuccessModal";
import { FailedFilesListQueryParam, failedFilesListUrl } from "../urls";

interface POUploadViewProps {
  params: FailedFilesListQueryParam;
}

const POUploadView: FC<POUploadViewProps> = ({ params }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const permissions = useUserPermissions();

  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>("");

  const isManageFailFile = permissions?.find(
    perm => perm.code === PermissionEnum.ALL_UPLOAD_PO_ADMIN
  );

  const { updateListSettings, settings } = useListSettings(
    ListViews.FILTER_ORDER
  );
  const [retryUpload] = useRetryUploadMutation();

  const [
    valuesPaginationState,
    setValuesPaginationState
  ] = useLocalPaginationState(settings?.rowNumber);
  const paginateValues = useLocalPaginator(setValuesPaginationState);

  const queryVariables = React.useMemo(
    () => ({
      ...valuesPaginationState,
      sortBy: getSortPoUploadFilterTable(params)
    }),
    [settings.rowNumber, valuesPaginationState, params]
  );

  const {
    data: dataFailedFiles,
    loading: loadingFailedFiles,
    refetch: refetchFailedFiles
  } = useFailedFilesQuery({ variables: queryVariables });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginateValues(
    dataFailedFiles?.failedFiles.pageInfo,
    valuesPaginationState
  );

  useEffect(() => {
    updateListSettings("rowNumber", 50);
  }, []);

  const resetPaginationState = () => {
    setValuesPaginationState({
      first: settings?.rowNumber,
      after: undefined,
      before: undefined
    });
  };

  const lastPaginationState = () => {
    setValuesPaginationState({
      last: dataFailedFiles?.failedFiles.latestPageItemNumber
    });
  };

  const handleNextLastPage = () => {
    lastPaginationState();
  };

  const handlePreviousFirstPage = () => {
    resetPaginationState();
  };

  const failedFiles = useMemo(
    () => mapEdgesToItems(dataFailedFiles?.failedFiles),
    [dataFailedFiles]
  );

  const handleSort = field => {
    resetPaginationState();
    navigate(
      failedFilesListUrl({
        ...params,
        ...getSortUrlVariables(field, params)
      }),
      { replace: true }
    );
  };

  const handleRetry = async (id: string) => {
    const { data } = await retryUpload({ variables: { id } });
    if (data?.retryUpload.status) {
      refetchFailedFiles();
    }
    return data?.retryUpload.status;
  };

  const handleUploadComplete = () => {
    refetchFailedFiles();
  };

  const handleOpenDialog = async (id: string, fileName: string) => {
    if (await handleRetry(id)) {
      setIsOpenDialog(true);
      setFileName(fileName);
    }
  };

  const handleCloseDialog = () => {
    setIsOpenDialog(false);
    setFileName("");
  };

  return (
    <Container>
      <PageHeader title={intl.formatMessage(sectionNames.poUpload)} />
      <POUploadPage onUploadComplete={handleUploadComplete} />
      {!!isManageFailFile &&
        (failedFiles?.length > 0 || loadingFailedFiles) && (
          <POFailFileTable
            data={failedFiles}
            disabled={loadingFailedFiles}
            pageInfo={pageInfo}
            onNextPage={loadNextPage}
            onPreviousPage={loadPreviousPage}
            onUpdateListSettings={updateListSettings}
            sort={getSortParams(params)}
            onSort={handleSort}
            settings={settings}
            onPreviousFirstPage={handlePreviousFirstPage}
            onNextLastPage={handleNextLastPage}
            totalCount={dataFailedFiles?.failedFiles.totalCount}
            latestPageItemNumber={
              dataFailedFiles?.failedFiles.latestPageItemNumber
            }
            onHandleOpenDialog={handleOpenDialog}
          />
        )}
      <RetrySuccessModal
        open={isOpenDialog}
        fileName={fileName}
        onClose={handleCloseDialog}
      />
    </Container>
  );
};

export default POUploadView;
